<template>
  <auth-layout class="authForm" :show-agreement="!isVerify" :show-support="!isVerify">
    <template #title>
      <div class="authForm_title">{{ $t('auth.form.titlePhone') }}</div>
    </template>
    <template #description>
      <div class="authForm_description">{{ isVerify ? $t('auth.form.descriptionPhoneVerify') : $t('auth.form.descriptionPhone') }}</div>
    </template>
    <template #content>
      <div class="authForm_content">
        <form class="authForm_form" @submit.prevent="handleContinue">
          <div class="authForm_input">
            <phone-input-v3
              :model-value="phone"
              @on-validate="onValidatePhone"
              @on-input="onInputPhone"
              placeholder="(000) 000 00 00"
              :label="$t('auth.phone')"
            />
            <div class="authForm_input_error" v-if="error">{{ error }}</div>
          </div>
          <div>
            <ac-button
              class="authForm_button-continue"
              type="submit"
              variant="tonal"
              block
              :disabled="isDisabledButton"
            >
            {{ isLoading ? $t('auth.form.buttonSend') : $t('continue') }}
              <div v-if="isLoading" class="authForm_preloader">
                <ac-preloader />
              </div>
            </ac-button>
          </div>
        </form>

        <template v-if="!isVerify">
          <auth-enter-with />

          <ac-button
            variant="text"
            color="gray"
            class="authForm_enter-email"
            block
            @click="handleEnterEmail"
          >
            {{ $t('auth.enterByEmail') }}
          </ac-button>
        </template>
      </div>
    </template>
  </auth-layout>
</template>

<script lang="ts" setup>
import AuthLayout from '@web/platform.v3/layouts/auth.vue'
import PhoneInputV3 from '@web/platform.v3/components/phoneInput.vue'
import AcButton from '@ui-kit/components/AcButton.vue'
import AcPreloader from '@ui-kit/components/AcPreloader.vue'
import AuthEnterWith from '@web/platform.v3/components/auth/enter-with.vue'

import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from '@web/store'
import { useAuthCatchErrors } from '@web/platform.v3/composables/useAuthCatchErrors'

type PhonePayload = {
  country: string
  countryCallingCode: string
  countryCode: string
  formatted: string
  getMetadata: () => void
  nationalNumber: string
  number: string
  possible: boolean
  valid: boolean
}

defineOptions({
  name: 'AuthPhoneForm'
})

const props = defineProps({
  isVerify: {
    type: Boolean,
    default: false
  }
})

const router = useRouter()
const store = useStore()

const phone = ref('')
const error = ref('')
const isLoading = ref(false)
const isDisabledButton = ref(true)

const storedPhone = computed(() => store.getters['authV3/phone'])
const isTimerActive = computed(() => store.getters['authV3/isTimerActive'])
const isSendCodeBySMS = computed(() => store.getters['authV3/isSendCodeBySMS'])

const { catchError } = useAuthCatchErrors()

function getPhoneValue (payload: string | PhonePayload | null | undefined): string {
  if (!payload) return ''
  if (typeof payload === 'string') return payload
  const number = payload.number || ''
  const formatted = payload.formatted || ''
  const countryCallingCode = payload.countryCallingCode || ''
  const nationalNumber = payload.nationalNumber || ''
  return number || formatted || `${countryCallingCode}${nationalNumber}` || ''
}

function onValidatePhone (payload: string | PhonePayload | null | undefined) {
  phone.value = getPhoneValue(payload)
  if (typeof payload === 'string') {
    isDisabledButton.value = payload.length === 0
  } else if (payload) {
    isDisabledButton.value = !(payload?.valid ?? false)
  }
}

function onInputPhone (payload: PhonePayload) {
  phone.value = getPhoneValue(payload)
}

const handleContinue = async () => {
  if (isLoading.value) return
  isLoading.value = true
  error.value = ''
  if (storedPhone.value === phone.value && isTimerActive.value) {
    store.commit('authV3/setNeedStartTimer', false)
    store.commit('authV3/setPhoneStatus', 'code')
    return
  }

  try {
    if (props.isVerify) {
      await store.dispatch('authV3/requestPhoneCodeForVerify', phone.value)
    } else {
      await store.dispatch('authV3/requestPhoneCode', { phone: phone.value, useSms: isSendCodeBySMS.value })
    }
    store.commit('authV3/setNeedStartTimer', true)
    store.commit('authV3/setIsSendCodeBySMS', false)
    store.commit('authV3/setPhone', phone.value)
    store.commit('authV3/setPhoneStatus', 'code')
  } catch (e) {
    error.value = catchError(e)
  } finally {
    isLoading.value = false
  }
}

function handleEnterEmail () {
  store.commit('authV3/reset')
  router.replace({ name: 'login', params: { type: 'email' } })
}

watch(storedPhone, () => {
  if (storedPhone.value) {
    phone.value = storedPhone.value
  }
}, {
  immediate: true,
  once: true
})

watch(() => props.isVerify, (newVal) => {
  store.commit('authV3/setIsSendCodeBySMS', newVal)
}, {
  immediate: true
})
</script>

<style lang="scss" scoped>
  @import '@web/platform.v3/styles/auth/form.scss';
</style>
